import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";

export default function NewTask() {
  const nav = useNavigate();
  const { data: pdata } = useQuery({
    queryKey: ["projects-mini"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const projects = pdata?.projects || [];

  const [form, setForm] = useState({
    project_id: "", title: "", description: "",
    type: "feature", priority: "normal", test_scope: "light",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (requestPlan = false) => {
    setErr("");
    if (!form.project_id || !form.title.trim()) {
      setErr("Project and title are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tasks", form);
      if (requestPlan) {
        await api.post(`/tasks/${data.task.id}/request-plan`);
        toast.success("Task created and plan requested.");
      } else {
        toast.success("Task saved as draft.");
      }
      nav(`/tasks/${data.task.id}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-2xl">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">New task</h1>

      <div className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="new-task-form">
        <div>
          <Label>Project</Label>
          <Select value={form.project_id} onValueChange={set("project_id")}>
            <SelectTrigger data-testid="nt-project"><SelectValue placeholder="Select a project" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title" data-testid="nt-title"
            value={form.title} onChange={(e) => set("title")(e.target.value)}
            placeholder="Short, action-oriented title"
          />
        </div>
        <div>
          <Label htmlFor="desc">Description</Label>
          <Textarea
            id="desc" data-testid="nt-description" rows={5}
            value={form.description} onChange={(e) => set("description")(e.target.value)}
            placeholder="What should the agent build? Links, success criteria, constraints…"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="nt-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feature">Feature</SelectItem>
                <SelectItem value="bug">Bug</SelectItem>
                <SelectItem value="content">Content</SelectItem>
                <SelectItem value="audit">Audit</SelectItem>
                <SelectItem value="fix">Fix</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={set("priority")}>
              <SelectTrigger data-testid="nt-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Test scope</Label>
            <Select value={form.test_scope} onValueChange={set("test_scope")}>
              <SelectTrigger data-testid="nt-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="full">Full</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {err && <div className="text-sm text-rose-500" data-testid="nt-error">{err}</div>}

        <div className="flex gap-2 flex-wrap pt-2">
          <Button variant="outline" onClick={() => submit(false)} disabled={busy} data-testid="nt-save-draft">
            Save as draft
          </Button>
          <Button onClick={() => submit(true)} disabled={busy} data-testid="nt-request-plan">
            Save & request plan
          </Button>
        </div>
      </div>
    </div>
  );
}
